// src/products/dataroom/components/Modals/AddUserModal.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, UserPlus, Mail, Building2, Users } from 'lucide-react';
import { supabase } from '../../../../config';
import partyTeamService from '../../services/partyTeamService';

const AddUserModal = ({ isOpen, onClose, onComplete }) => {
  const [projects, setProjects] = useState([]);
  const [parties, setParties] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedParty, setSelectedParty] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [users, setUsers] = useState([{ email: '', firstName: '', lastName: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedProject) {
      loadParties(selectedProject);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedParty) {
      loadTeams(selectedParty);
    }
  }, [selectedParty]);

  const loadProjects = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('User not authenticated');
        setProjects([]);
        return;
      }

      // Load projects for the current user using the same method as other components
      const { data, error } = await supabase
        .from('dataroom_projects')
        .select(`
          id,
          project_name,
          dataroom_users!inner(role)
        `)
        .eq('dataroom_users.user_id', user.id)
        .order('project_name');

      if (error) throw error;
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects. Please try again.');
    }
  };

  const loadParties = async (projectId) => {
    try {
      const { data, error } = await supabase
        .from('dataroom_parties')
        .select('id, name, code, color')
        .eq('project_id', projectId)
        .order('name');

      if (error) throw error;
      setParties(data || []);
      setSelectedParty('');
      setTeams([]);
    } catch (err) {
      console.error('Error loading parties:', err);
    }
  };

  const loadTeams = async (partyId) => {
    try {
      const { data, error } = await supabase
        .from('dataroom_teams')
        .select('id, name, code')
        .eq('party_id', partyId)
        .order('name');

      if (error) throw error;
      setTeams(data || []);
      setSelectedTeam('');
    } catch (err) {
      console.error('Error loading teams:', err);
    }
  };

  const addUserRow = () => {
    setUsers([...users, { email: '', firstName: '', lastName: '' }]);
  };

  const removeUserRow = (index) => {
    if (users.length > 1) {
      setUsers(users.filter((_, i) => i !== index));
    }
  };

  const updateUser = (index, field, value) => {
    const updated = [...users];
    updated[index][field] = value;
    setUsers(updated);
  };

  const handleSubmit = async () => {
    if (!selectedProject) {
      setError('Please select a dataroom project');
      return;
    }

    if (!selectedParty) {
      setError('Please select a party');
      return;
    }

    const validUsers = users.filter(u => u.email.trim());
    if (validUsers.length === 0) {
      setError('Please add at least one user with an email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get current user for assignment tracking
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const savedUsers = [];

      for (const user of validUsers) {
        const userEmail = user.email.trim();
        
        // Check if user exists in auth.users
        const { data: existingAuthUser } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('email', userEmail)
          .single();

        const existingUserId = existingAuthUser?.id || null;

        // Create dataroom_users entry
        const { data: dataroomUser, error: userError } = await supabase
          .from('dataroom_users')
          .insert({
            email: userEmail,
            user_id: existingUserId,
            project_id: parseInt(selectedProject),
            role: 'viewer',
            status: existingUserId ? 'active' : 'pending',
            invited_at: new Date().toISOString(),
            metadata: {
              party_id: parseInt(selectedParty),
              team_id: selectedTeam ? parseInt(selectedTeam) : null,
              first_name: user.firstName.trim() || null,
              last_name: user.lastName.trim() || null,
              added_via_manual: true
            }
          })
          .select()
          .single();

        if (userError) {
          if (userError.code === '23505') {
            console.log(`User ${userEmail} already exists in this project`);
            continue;
          }
          console.error(`Failed to add ${userEmail}:`, userError);
          continue;
        }

        // If user exists in auth, create party/team assignments immediately
        if (existingUserId) {
          console.log(`User ${userEmail} exists in system, creating assignments...`);
          
          // Assign to party
          const partyResult = await partyTeamService.assignUserToParty(
            existingUserId,
            parseInt(selectedProject),
            parseInt(selectedParty),
            currentUser?.id || null
          );

          if (partyResult.error) {
            console.error(`Failed to assign ${userEmail} to party:`, partyResult.error);
          } else {
            console.log(`✓ Assigned ${userEmail} to party`);
          }

          // Assign to team if selected
          if (selectedTeam) {
            const teamResult = await partyTeamService.assignUserToTeam(
              existingUserId,
              parseInt(selectedProject),
              parseInt(selectedTeam),
              currentUser?.id || null
            );

            if (teamResult.error) {
              console.error(`Failed to assign ${userEmail} to team:`, teamResult.error);
            } else {
              console.log(`✓ Assigned ${userEmail} to team`);
            }
          }
        } else {
          console.log(`User ${userEmail} is pending - assignments will be created when they accept invite`);
        }

        savedUsers.push(dataroomUser);
      }

      if (savedUsers.length > 0) {
        onComplete && onComplete(savedUsers);
        handleClose();
      } else {
        setError('Failed to add users. They may already exist in this project.');
      }
    } catch (err) {
      console.error('Error adding users:', err);
      setError(err.message || 'Failed to add users');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedProject('');
    setSelectedParty('');
    setSelectedTeam('');
    setUsers([{ email: '', firstName: '', lastName: '' }]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const selectedPartyData = parties.find(p => p.id === parseInt(selectedParty));

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <UserPlus className="w-6 h-6 text-white mr-3" />
            <h2 className="text-xl font-bold text-white">Add Users to Dataroom</h2>
          </div>
          <button onClick={handleClose} className="text-white hover:text-blue-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Selection Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Project Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-1" />
                Dataroom Project *
              </label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.project_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Party Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-1" />
                Party *
              </label>
              <select
                value={selectedParty}
                onChange={(e) => setSelectedParty(e.target.value)}
                disabled={!selectedProject}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Party...</option>
                {parties.map(party => (
                  <option key={party.id} value={party.id}>
                    {party.code} - {party.name}
                  </option>
                ))}
              </select>
              {selectedPartyData && (
                <div className="mt-2 flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: selectedPartyData.color }}
                  />
                  <span className="text-xs text-gray-600">Selected party color</span>
                </div>
              )}
            </div>

            {/* Team Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Team (Optional)
              </label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                disabled={!selectedParty}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Party Level (No Team)</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.code} - {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Users Section */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">
                <Mail className="w-4 h-4 inline mr-1" />
                Users to Add
              </label>
              <button
                onClick={addUserRow}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Another User
              </button>
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {users.map((user, index) => (
                <div key={index} className="flex gap-2 items-start bg-gray-50 p-3 rounded-lg">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input
                      type="email"
                      placeholder="Email *"
                      value={user.email}
                      onChange={(e) => updateUser(index, 'email', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="First Name"
                      value={user.firstName}
                      onChange={(e) => updateUser(index, 'firstName', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={user.lastName}
                      onChange={(e) => updateUser(index, 'lastName', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    onClick={() => removeUserRow(index)}
                    disabled={users.length === 1}
                    className="p-2 text-red-600 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Users will be added with "Pending" status. 
              Go to the Users page to send invitation emails.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedProject || !selectedParty}
            className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Adding Users...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                Add {users.filter(u => u.email.trim()).length} User(s)
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddUserModal;

